import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid } from 'v3/Components/DataGrid/DataGrid';
import { DateRangePickerFilterFilter } from 'v3/Components/DataGrid/ColumnFilters/DateRangePickerFilter';
import { MultiSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/MultiSelectFilter';
import { SingleSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/SingleSelectFilter';
import { TextFieldFilter } from 'v3/Components/DataGrid/ColumnFilters/TextFieldFilter';
import { StatusPill } from 'v3/Components/Shared/StatusPill';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { buildEndpoint, buildRoute, defaultDataTransform, useApiWorker, useDataFetcher, formatShortDateTime, useHasPermissions } from 'Utilities';
import { Permission } from 'Models/Templates/Permission/Permission';
import { useEnums, useGlobalContext, useToasts } from 'Context';
import { Link, useHistory } from 'react-router-dom';
import { AddEditSubRoute, ROUTES } from 'Navigation';
import ClientSelector from 'v3/Components/Shared/ClientSelector';
import useClientSearchParams from 'v3/Utilities/useClientSearchParams';
import NewReferralButton from 'v3/Components/Shared/NewReferralButton';
import './IntakeQueue.scss';

interface DropdownOptions {
  clients: { label: string; value: string }[];
  owners: { label: string; value: string }[];
  services: { label: string; value: string }[];
  subServices: { label: string; value: string }[];
  adjusters: { label: string; value: string }[];
  proxiedByUsers: string[];
  states: { label: string; value: string }[];
  specialties: { label: string; value: string }[];
  tatPriorities: { label: string; value: string }[];
  tats: { label: string; value: string }[];
  lobs: { label: string; value: string }[];
  apContactPreferences: { label: string; value: string }[];
  statuses: { label: string; value: string }[];
}

const renderSpecialtyCell = (params: any) => {
  const specialties: { displayName: string }[] = params.row.requestedSpecialty ?? [];
  if (specialties.length === 0) return null;
  return (
    <span className="specialty-cell">
      <span>{specialties[0]?.displayName}</span>
      {specialties.length > 1 && (
        <span className="specialty-cell__extra">+{specialties.length - 1}</span>
      )}
    </span>
  );
};

const StatusCell: React.FC<{ value: any; row: any }> = ({ value, row }) => {
  const hasPermission = useHasPermissions();

  if (value === 'Pending' && hasPermission([Permission.ConvertReferralRequest])) {
    return (
      <Link
        className="color-primary"
        to={buildRoute(ROUTES.REFERRALREQUESTPROPOSALS, row.id)}
        onClick={(e) => e.stopPropagation()}
      >
        Convert to Claimant
      </Link>
    );
  }

  return value ? <StatusPill status={value} /> : null;
};

interface ActionHandlers {
  onDelete: (id: string) => Promise<void>;
  canDelete: boolean;
}

const buildColumns = (opts: DropdownOptions, userdata: any, actions: ActionHandlers): any[] => {
  const isInternal = userdata?.isInternal ?? false;

  const columns: Array<{ _show?: boolean; [key: string]: any }> = [
    {
      field: 'stradixNumber',
      headerName: 'Stradix #',
      width: 188,
      hideable: false,
      pinnable: false,
      cellClassName: 'intake-queue-cell-bold',
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'claimant',
      headerName: 'Claimant',
      width: 200,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'client',
      headerName: 'Client',
      width: 200,
      type: 'singleSelect',
      _show: isInternal,
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.clients} />,
    },
    {
      field: 'claimNumber',
      headerName: 'Claim/Client #',
      width: 160,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'referralDate',
      headerName: 'Referral Date',
      width: 200,
      widthOnFilter: 260,
      valueFormatter: (value: string) => formatShortDateTime(value),
      renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
    },
    {
      field: 'dueDate',
      headerName: 'Client Due Date',
      width: 200,
      widthOnFilter: 260,
      valueFormatter: (value: string) => formatShortDateTime(value),
      renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
    },
    {
      field: 'tatPriority',
      headerName: 'TAT Priority',
      width: 140,
      type: 'singleSelect',
      renderCell: (params: any) =>
        params.value ? <StatusPill status={params.value} /> : null,
      renderHeaderFilter: (params: any) => <SingleSelectFilter {...params} optionItems={opts.tatPriorities} />,
    },
    {
      field: 'benefitState',
      headerName: 'State',
      width: 140,
      type: 'singleSelect',
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.states} />,
    },
    {
      field: 'owner',
      headerName: 'Owner',
      width: 180,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'intakeOwnerName',
      headerName: 'Intake Owner',
      width: 180,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'recentUser',
      headerName: 'Recent User',
      width: 180,
      sortable: false,
      filterable: false,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 160,
      type: 'singleSelect',
      renderCell: (params: any) => <StatusCell value={params.value} row={params.row} />,
      renderHeaderFilter: (params: any) => <SingleSelectFilter {...params} optionItems={opts.statuses} />,
    },
    {
      field: 'subService',
      headerName: 'Sub Service',
      width: 220,
      type: 'singleSelect',
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.subServices} />,
    },
    {
      field: 'service',
      headerName: 'Service',
      width: 200,
      type: 'singleSelect',
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.services} />,
    },
    {
      field: 'requestedSpecialty',
      headerName: 'Requested Specialty',
      width: 220,
      sortable: false,
      type: 'singleSelect',
      renderCell: renderSpecialtyCell,
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.specialties} />,
    },
    {
      field: 'lob',
      headerName: 'Line of Business',
      width: 260,
      type: 'singleSelect',
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.lobs} />,
    },
    {
      field: 'apContactPreference',
      headerName: 'AP Contact Pref.',
      width: 160,
      type: 'singleSelect',
      renderHeaderFilter: (params: any) => <SingleSelectFilter {...params} optionItems={opts.apContactPreferences} />,
    },
    {
      field: 'numberOfReviews',
      headerName: '# of APs',
      width: 120,
      type: 'number',
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} inputType="number" />,
    },
    {
      field: 'referralSource',
      headerName: 'Referrer',
      width: 180,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'adjuster',
      headerName: 'Adjuster',
      width: 180,
      type: 'singleSelect',
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.adjusters} />,
    },
    {
      field: 'proxiedBy',
      headerName: 'Proxied By',
      width: 180,
      type: 'singleSelect',
      _show: isInternal,
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.proxiedByUsers} />,
    },
    // Hidden by default
    {
      field: 'referralNumber',
      headerName: 'Referral #',
      width: 160,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'tat',
      headerName: 'TAT',
      width: 140,
      type: 'singleSelect',
      renderHeaderFilter: (params: any) => <SingleSelectFilter {...params} optionItems={opts.tats} />,
    },
    {
      field: 'employerName',
      headerName: 'Employer Name',
      width: 200,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'actions',
      headerName: ' ',
      width: 60,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      disableReorder: true,
      pinnable: false,
      renderCell: (params: any) =>
        actions.canDelete && params.row.status === 'Converted' ? (
          <div className="intake-queue-action-cell">
            <button
              className="intake-queue-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                actions.onDelete(params.row.id);
              }}
              aria-label="Delete referral"
            >
              <DeleteIcon />
            </button>
          </div>
        ) : null,
    },
  ];

  return columns
    .filter(({ _show }) => _show === undefined || _show)
    .map(({ _show, ...col }) => col);
};

const useIntakeQueueDropdownOptions = (): DropdownOptions => {
  const enums: any = useEnums();
  const filterOptionsEndpoint = ENDPOINTS.intakequeue.endpointString('FilterOptions');

  const [clientsList] = useDataFetcher<{ id: string; name: string }[]>(
    buildEndpoint(ENDPOINTS.clients.dropdownItems),
    [],
    defaultDataTransform,
  );
  const [filterOptions] = useDataFetcher<{
    owners: { id: string; name: string }[];
    services: { id: string; name: string }[];
    subServices: { id: string; name: string }[];
    adjusters: { id: string; name: string }[];
    proxiedByUsers: string[];
  }>(
    buildEndpoint(filterOptionsEndpoint),
    { owners: [], services: [], subServices: [], adjusters: [], proxiedByUsers: [] },
    defaultDataTransform,
    undefined,
    true,
    '3.0',
  );

  return {
    clients: (clientsList ?? []).map((c) => ({ label: c.name, value: c.id })).filter((c) => c.label && c.value),
    owners: (filterOptions?.owners ?? []).map((o) => ({ label: o.name, value: o.id })),
    services: (filterOptions?.services ?? []).map((s) => ({ label: s.name, value: s.id })),
    subServices: (filterOptions?.subServices ?? []).map((s) => ({ label: s.name, value: s.id })),
    adjusters: (filterOptions?.adjusters ?? []).map((a) => ({ label: a.name, value: a.id })),
    proxiedByUsers: filterOptions?.proxiedByUsers ?? [],
    states: (enums?.state ? Object.values(enums.state) : []).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    specialties: Object.values(enums?.specialty ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    tatPriorities: Object.values(enums?.tatPriority ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    tats: Object.values(enums?.tat ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    lobs: Object.values(enums?.lineOfBusiness ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    apContactPreferences: Object.values(enums?.apContact ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    statuses: Object.values(enums?.referralsRequestStatus ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
  };
};

export const IntakeQueue: React.FC = () => {
  const history = useHistory();
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const isInternalUser = userdata?.isInternal || userdata?.isAdmin;
  const readEndpoint = buildEndpoint(ENDPOINTS.intakequeue.read);
  const exportEndpoint = buildEndpoint(ENDPOINTS.intakequeue.endpointString('ExportToCsv'));
  const dropdownOptions = useIntakeQueueDropdownOptions();
  const API = useApiWorker();
  const toast = useToasts();
  const hasPermissions = useHasPermissions();
  const canDelete = hasPermissions([Permission.DeleteReferralsRequest]) ?? false;
  const [refreshKey, setRefreshKey] = React.useState(0);

  const handleDeleteReferral = React.useCallback(async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    try {
      await API.delete(`${ENDPOINTS.referralsRequests.base}/${id}`);
      setRefreshKey((k) => k + 1);
    } catch (error: any) {
      toast.error(error?.response?.data?.errorMessage ?? 'Failed to delete referral.');
    }
  }, [API, toast]);

  const columns = React.useMemo(
    () => buildColumns(dropdownOptions, userdata, { onDelete: handleDeleteReferral, canDelete }),
    [dropdownOptions, userdata, handleDeleteReferral, canDelete],
  );

  const legacyParams = React.useMemo(() => ({
    readEndpoint,
    apiVersion: '3.0',
  }), [readEndpoint]);

  const { setSelectedClient, clientSearchParams: baseClientSearchParams } =
    useClientSearchParams();

  // _refresh is a deliberate cache-buster: bumping refreshKey changes this object's
  // identity so the grid refetches after an intake action.
  const clientSearchParams = React.useMemo(
    () => ({ ...baseClientSearchParams, _refresh: refreshKey }),
    [baseClientSearchParams, refreshKey],
  );

  const handleRowClick = (params: any) => {
    const { referralId, id, clientId, lineOfBusinessValue, referralSourceId } = params.row ?? {};

    if (referralId) {
      history.push(buildRoute(AddEditSubRoute.Referral, referralId, ROUTES.VIEW));
      return;
    }

    if (isInternalUser) {
      history.push(
        buildRoute(
          ROUTES.ADD_EDIT,
          AddEditSubRoute.ReferralsRequest,
          id +
            '?lob=' + (lineOfBusinessValue ?? '') +
            '&draft=0&clientId=' + (clientId ?? '') +
            (referralSourceId ? '&referrerId=' + referralSourceId : ''),
        ),
      );
    } else {
      history.push(buildRoute(AddEditSubRoute.ReferralsRequest, id, ROUTES.VIEW));
    }
  };

  return (
    <div className='intakeQueue'>
      <div className="main-grid-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h1>Intake Queue</h1>
        </div>
        <NewReferralButton text="Add Case" startIcon={<AddIcon />} />
      </div>
      <div className="reviewers-content">
        <DataGrid
          onRowClick={handleRowClick}
          title='Intake Queue'
          gridId='intakeQueueGrid'
          enableUrlSync
          legacyParams={legacyParams}
          columns={columns}
          pinnedColumns={{ left: ['stradixNumber'] }}
          additionalSearchParams={clientSearchParams}
          exportEndpoint={exportEndpoint}
          exportFileName="IntakeQueue"
          defaultShowFilters={isInternalUser}
          columnVisibilityModel={{
            referralNumber: false,
            tat: false,
            employerName: false,
          }}
          enableAutoRefresh
        />
      </div>
    </div>
  );
};
